import type { CursorState } from '../../types';
import { DomHelpers } from "@/utils/DomHelpers";

/**
 * Custom cursor that follows mouse over hero slider video links
 */
export class HeroCursor {
  private readonly slider: HTMLElement;
  private readonly cursor: HTMLElement;
  private readonly targets: Element[];
  private state: CursorState;
  private rafId = 0;
  private centerTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(slider: HTMLElement) {
    this.slider = slider;
    const c = slider.querySelector<HTMLElement>('.cursor-title');
    if (!c) throw new Error('HeroCursor: .cursor-title not found');
    this.cursor = c;
    this.targets = Array.from(
      slider.querySelectorAll('.video-link, .video-link .no-title')
    );
    const w = slider.clientWidth / 2;
    const h = slider.clientHeight / 2;
    this.state = { x: w, y: h, tx: w, ty: h, visible: false };
  }

  init(): void {
    if (!DomHelpers.hasHover()) return;

    const loop = (): void => {
      this.state.x = DomHelpers.lerp(this.state.x, this.state.tx, 0.18);
      this.state.y = DomHelpers.lerp(this.state.y, this.state.ty, 0.18);
      this.cursor.style.left = this.state.x + 'px';
      this.cursor.style.top = this.state.y + 'px';
      this.rafId = requestAnimationFrame(loop);
    };
    loop();

    this.targets.forEach((el) => {
      el.addEventListener('mouseenter', this.show, { passive: true });
      el.addEventListener('mousemove', this.onMove, { passive: true });
      el.addEventListener('mouseleave', this.hide, { passive: true });
    });

    window.addEventListener('resize', this.onResize, { passive: true });
  }

  private toCenter = (): void => {
    const r = this.slider.getBoundingClientRect();
    this.state.tx = r.width / 2;
    this.state.ty = r.height / 2;
  };

  private show = (): void => {
    if (this.centerTimer) clearTimeout(this.centerTimer);
    if (this.state.visible) return;
    this.state.visible = true;
    this.cursor.classList.add('is-show');
  };

  private hide = (): void => {
    this.state.visible = false;
    this.cursor.classList.remove('is-show');
    if (this.centerTimer) clearTimeout(this.centerTimer);
    this.centerTimer = setTimeout(this.toCenter, 380);
  };

  private onMove = (e: Event): void => {
    const me = e as MouseEvent;
    const r = this.slider.getBoundingClientRect();
    this.state.tx = me.clientX - r.left;
    this.state.ty = me.clientY - r.top;
  };

  private onResize = (): void => {
    this.toCenter();
    if (!this.state.visible) {
      this.cursor.style.left = this.state.tx + 'px';
      this.cursor.style.top = this.state.ty + 'px';
    }
  };
}
